import { IDeviceConfiguration } from "@formant/core/model/IDeviceConfiguration";
import { IRtcPeer } from "@formant/core/model/IRtcPeer";
import { RtcConnectionStatus } from "@formant/core/model/RtcConnectionStatus";
import { Timestamp } from "@formant/core/model/Timestamp";
import { Uuid } from "@formant/core/model/Uuid";
import { IIceMode } from "@formant/rtc-client/models/IIceMode";
import { IPingInfo } from "@formant/rtc-client/models/IPingInfo";
import { IRtcConnectionStatsInfo } from "@formant/rtc-client/models/IRtcConnectionStatsInfo";
import { IRtcSendConfiguration } from "@formant/rtc-client/models/IRtcSendConfiguration";
import { IRtcSessionMetricsMessageCounts } from "@formant/rtc-client/models/IRtcSessionMetricsMessageCounts";
import { IRtcStreamMessage } from "@formant/rtc-client/models/IRtcStreamMessage";
import { IStreamControl } from "@formant/rtc-client/models/IStreamControl";
import { RtcClient } from "@formant/rtc-client/RtcClient";
import { RtcClientV1 } from "@formant/rtc-client/RtcClientV1";

export type SessionLifecycle =
    | "noSession"
    | "hasSession"
    | "confirmed"
    | "started"
    | "error"
    | "disconnected";

export interface ITeleopSession {
    deviceId: Uuid;
    rtcClient?: RtcClient | RtcClientV1;
    agentVersion?: string;
    sessionId: Uuid;
    sessionStatus: SessionLifecycle;
    connectionStatus: RtcConnectionStatus;
    ping?: number;
    devicePeer: false | undefined | IRtcPeer;
    ownerUserPeer: IRtcPeer | null | undefined;
    owner: boolean;
    iceMode?: IIceMode;
    deviceConfiguration?: IDeviceConfiguration;
    originalDeviceConfiguration?: IDeviceConfiguration;
    updatedDeviceConfiguration?: IDeviceConfiguration;
    startedTimestamp?: Timestamp;
    connectedTimestamp?: Timestamp;
    hasStarted: boolean;
    hasStopped: boolean;
    uuid: Uuid;

    degradeVideoQuality(streamName: string): void;

    setLowBandwidthMode(enable: boolean): void;

    start(): Promise<void>;

    signalingVersion(): number | undefined;

    isRtcV2(_: RtcClientV1 | RtcClient): _ is RtcClient;

    restart(): Promise<void>;

    setDisconnectIntent(): void;

    join(): Promise<void>;

    stop(): Promise<void>;

    getDeviceConfiguration(): Promise<IDeviceConfiguration | undefined>;

    getPingInfo(): IPingInfo | undefined;

    getConnectionStatsInfo(): Promise<IRtcConnectionStatsInfo | undefined>;

    getPing(): Promise<number | undefined>;

    getLastMessageTimestamp(): Timestamp | undefined;

    getSessionMetricsMessageCounts():
        | IRtcSessionMetricsMessageCounts
        | undefined;

    controlRemoteStream(streamControl: IStreamControl): void;

    send(msg: IRtcStreamMessage, config?: IRtcSendConfiguration): void;

    registerReceiver(
        streamName: string,
        receiver: (msg: IRtcStreamMessage) => void,
    ): () => boolean;

    getConnectionStatus(): RtcConnectionStatus;

    disableControls(): void;

    createDefaultTeleopDeviceConfiguration(): Promise<void>;

    updateDeviceConfiguration(): Promise<void>;
}
